import { z } from 'zod';

import {
  inspectionReportDocumentSchema,
  inspectionReportStatusSchema,
  type InspectionLayoutId,
  type BlockVAlign,
} from '../schemas/inspectionReport';

// Vertical alignment → flex justify-content. Shared by the editor and the PDF
// generator so a text region sits at the same vertical position in both.
export function vAlignToJustify(vAlign?: BlockVAlign): 'flex-start' | 'center' | 'flex-end' {
  return vAlign === 'middle' ? 'center' : vAlign === 'bottom' ? 'flex-end' : 'flex-start';
}

// ============================================================================
// Inspection report layout registry
//
// The single source of truth for the per-page layouts. Both the React editor
// and the server-side PDF generator consume this registry — they iterate the
// same slot/block keys and stamp the same class names, and the geometry lives
// in one shared CSS string (INSPECTION_LAYOUT_CSS). That is what makes the
// editor and the PDF render identical pages.
//
// Adding a layout = add an entry here + a CSS block below. Nothing else changes.
// ============================================================================

export interface LayoutSlotDef {
  key: string;
}

export interface LayoutBlockDef {
  key: string;
  defaultStyle: 'heading' | 'body';
}

export interface LayoutDefinition {
  id: InspectionLayoutId;
  label: string;
  slots: LayoutSlotDef[];
  blocks: LayoutBlockDef[];
}

export const INSPECTION_LAYOUTS: Record<InspectionLayoutId, LayoutDefinition> = {
  hero: {
    id: 'hero',
    label: 'Hero — 1 photo + text',
    slots: [{ key: 'slot-1' }],
    blocks: [{ key: 'block-header', defaultStyle: 'heading' }, { key: 'block-body', defaultStyle: 'body' }],
  },
  'two-stack': {
    id: 'two-stack',
    label: '2 photos stacked',
    slots: [{ key: 'slot-1' }, { key: 'slot-2' }],
    blocks: [{ key: 'block-header', defaultStyle: 'heading' }],
  },
  'two-text': {
    id: 'two-text',
    label: '2 photos + side text',
    slots: [{ key: 'slot-1' }, { key: 'slot-2' }],
    blocks: [{ key: 'block-header', defaultStyle: 'heading' }, { key: 'block-side', defaultStyle: 'body' }],
  },
  three: {
    id: 'three',
    label: '3 photos (1 + 2)',
    slots: [{ key: 'slot-1' }, { key: 'slot-2' }, { key: 'slot-3' }],
    blocks: [{ key: 'block-header', defaultStyle: 'heading' }, { key: 'block-body', defaultStyle: 'body' }],
  },
  'two-row-text': {
    id: 'two-row-text',
    label: '2 rows — photo + text',
    slots: [{ key: 'slot-1' }, { key: 'slot-2' }],
    blocks: [
      { key: 'block-header', defaultStyle: 'heading' },
      { key: 'block-side-1', defaultStyle: 'body' },
      { key: 'block-side-2', defaultStyle: 'body' },
    ],
  },
  'three-row-text': {
    id: 'three-row-text',
    label: '3 rows — photo + text',
    slots: [{ key: 'slot-1' }, { key: 'slot-2' }, { key: 'slot-3' }],
    blocks: [
      { key: 'block-header', defaultStyle: 'heading' },
      { key: 'block-side-1', defaultStyle: 'body' },
      { key: 'block-side-2', defaultStyle: 'body' },
      { key: 'block-side-3', defaultStyle: 'body' },
    ],
  },
  'four-row-text': {
    id: 'four-row-text',
    label: '4 rows — photo + text',
    slots: [{ key: 'slot-1' }, { key: 'slot-2' }, { key: 'slot-3' }, { key: 'slot-4' }],
    blocks: [
      { key: 'block-header', defaultStyle: 'heading' },
      { key: 'block-side-1', defaultStyle: 'body' },
      { key: 'block-side-2', defaultStyle: 'body' },
      { key: 'block-side-3', defaultStyle: 'body' },
      { key: 'block-side-4', defaultStyle: 'body' },
    ],
  },
  text: {
    id: 'text',
    label: 'Text only',
    slots: [],
    blocks: [{ key: 'block-header', defaultStyle: 'heading' }, { key: 'block-body', defaultStyle: 'body' }],
  },
};

export function getLayout(id: InspectionLayoutId): LayoutDefinition {
  const layout = INSPECTION_LAYOUTS[id];
  if (!layout) throw new Error(`Unknown inspection layout: ${id}`);
  return layout;
}

export function layoutSlotKeys(id: InspectionLayoutId): string[] {
  return getLayout(id).slots.map((s) => s.key);
}

export function layoutBlockKeys(id: InspectionLayoutId): string[] {
  return getLayout(id).blocks.map((b) => b.key);
}

// Class-name helpers — the parity contract. Editor and PDF both stamp these.
export const layoutClass = (id: InspectionLayoutId): string => `ins-layout-${id}`;
export const slotClass = (key: string): string => `ins-slot-${key}`;
export const blockClass = (key: string): string => `ins-block-${key}`;

// ============================================================================
// Geometry — the single source of layout truth, imported by the frontend and
// inlined into the PDF <head>. Keyed by .ins-layout-${id} / .ins-slot-${key} /
// .ins-block-${key}. Page sizing (Letter) is handled by the page container and
// @page rules in the renderers; this file owns the intra-page arrangement.
// ============================================================================

export const INSPECTION_LAYOUT_CSS = `
.ins-page {
  /* Height every slot reserves above its photo for the caption line (13px text
     at 1.35 + 6px below it). Side text blocks subtract the same amount so they
     start level with the photo, not with the slot. */
  --ins-caption-band: 23.55px;
  display: grid;
  gap: 24px;
  /* Tighter top/bottom than the sides: the PDF renderer already adds its own
     physical page margins there, so 48px all round read as a huge dead band. */
  padding: 24px 48px;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  position: relative;
}
/* Page-level annotation overlay — spans the whole page above slots/blocks.
   0..1 coords map into a 0 0 100 100 viewBox stretched to the page. */
.ins-page-anno-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
/* A slot is a column: the caption, then the photo's frame under it. The frame is
   what clips/holds the photo (and the annotation overlay, whose 0..1 coords are
   relative to it) — the caption must stay outside that box, or it would be drawn
   over the image. */
.ins-slot {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ins-slot-frame {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #eef1f4;
  border-radius: 4px;
}
/* Caption lives here (not in the editor/PDF stylesheets) so both renderers get
   the same one. Both renderers emit this element for EVERY slot, empty or not,
   and min-height reserves one line — so a photo is the same size whether or not
   it is captioned. (A caption long enough to wrap does take a second line from
   its own photo.) The editor adds its own click-to-edit affordances on top of
   this class; the PDF renders the caption text alone. */
.ins-caption {
  flex: none;
  padding-bottom: 6px;
  min-height: var(--ins-caption-band);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  color: #374151;
}
.ins-block {
  min-height: 0;
  overflow: hidden;
}
/* In the row layouts (below) photos are left-aligned, which puts the blank space
   beside a portrait photo under the frame's centre — the point the zoom scales
   about. So a zoomed portrait photo magnified the blank space instead of the
   picture. Zooming in centres the photo; zooming all the way back out returns it
   to the shared left edge. Three selectors' worth of specificity, so it outranks
   both the row-layout rule and the plain .ins-photo rule the renderers declare,
   and the editor and the PDF switch at exactly the same zoom. */
.ins-photo-layer[data-zoomed] .ins-photo { object-position: center center; }

/* Optional per-page header text — always the top row, spanning all columns. */
.ins-block-block-header { grid-column: 1 / -1; grid-row: 1; font-weight: 600; }

/* Text that sits BESIDE a photo shares its grid row, so without this it would
   start level with the caption line above the picture instead of with the
   picture. Blocks that sit ABOVE or BELOW photos (header, body) are left alone. */
.ins-block-block-side,
.ins-block-block-side-1,
.ins-block-block-side-2,
.ins-block-block-side-3,
.ins-block-block-side-4 { margin-top: var(--ins-caption-band); }

/* hero — header, one large photo, paragraph below */
.ins-layout-hero { grid-template-rows: auto 1fr auto; }
.ins-layout-hero .ins-slot-slot-1 { grid-row: 2; }
.ins-layout-hero .ins-block-block-body { grid-row: 3; }

/* two-stack — header, two photos top to bottom */
.ins-layout-two-stack { grid-template-rows: auto 1fr 1fr; }
.ins-layout-two-stack .ins-slot-slot-1 { grid-row: 2; }
.ins-layout-two-stack .ins-slot-slot-2 { grid-row: 3; }

/* two-text — header, photos in the left column, text on the side */
.ins-layout-two-text { grid-template-columns: 1fr 1fr; grid-template-rows: auto 1fr 1fr; }
.ins-layout-two-text .ins-slot-slot-1 { grid-column: 1; grid-row: 2; }
.ins-layout-two-text .ins-slot-slot-2 { grid-column: 1; grid-row: 3; }
.ins-layout-two-text .ins-block-block-side { grid-column: 2; grid-row: 2 / 4; }

/* three — header, one wide photo on top, two below, paragraph at the foot */
.ins-layout-three { grid-template-columns: 1fr 1fr; grid-template-rows: auto 1.4fr 1fr auto; }
.ins-layout-three .ins-slot-slot-1 { grid-column: 1 / 3; grid-row: 2; }
.ins-layout-three .ins-slot-slot-2 { grid-column: 1; grid-row: 3; }
.ins-layout-three .ins-slot-slot-3 { grid-column: 2; grid-row: 3; }
.ins-layout-three .ins-block-block-body { grid-column: 1 / 3; grid-row: 4; }

/* Row layouts only: portrait and landscape photos share a left edge so the
   photo column lines up with the text beside it. Every other layout keeps the
   centred default from the editor / PDF stylesheets (two class names here beats
   their bare .ins-photo on specificity, whatever the sheet order). */
.ins-layout-two-row-text .ins-photo,
.ins-layout-three-row-text .ins-photo,
.ins-layout-four-row-text .ins-photo { object-position: left center; }

/* two-row-text — header, then two rows of [photo | text] */
.ins-layout-two-row-text { grid-template-columns: 1fr 1fr; grid-template-rows: auto 1fr 1fr; }
.ins-layout-two-row-text .ins-slot-slot-1 { grid-column: 1; grid-row: 2; }
.ins-layout-two-row-text .ins-block-block-side-1 { grid-column: 2; grid-row: 2; }
.ins-layout-two-row-text .ins-slot-slot-2 { grid-column: 1; grid-row: 3; }
.ins-layout-two-row-text .ins-block-block-side-2 { grid-column: 2; grid-row: 3; }

/* three-row-text — header, then three rows of [photo | text] */
.ins-layout-three-row-text { grid-template-columns: 1fr 1fr; grid-template-rows: auto 1fr 1fr 1fr; }
.ins-layout-three-row-text .ins-slot-slot-1 { grid-column: 1; grid-row: 2; }
.ins-layout-three-row-text .ins-block-block-side-1 { grid-column: 2; grid-row: 2; }
.ins-layout-three-row-text .ins-slot-slot-2 { grid-column: 1; grid-row: 3; }
.ins-layout-three-row-text .ins-block-block-side-2 { grid-column: 2; grid-row: 3; }
.ins-layout-three-row-text .ins-slot-slot-3 { grid-column: 1; grid-row: 4; }
.ins-layout-three-row-text .ins-block-block-side-3 { grid-column: 2; grid-row: 4; }

/* four-row-text — header, then four rows of [photo | text]. Same shape as the
   three-row layout with one more row; the tighter 16px gap buys the photos back
   some of the height the extra row costs. */
.ins-layout-four-row-text { grid-template-columns: 1fr 1fr; grid-template-rows: auto 1fr 1fr 1fr 1fr; gap: 16px; }
.ins-layout-four-row-text .ins-slot-slot-1 { grid-column: 1; grid-row: 2; }
.ins-layout-four-row-text .ins-block-block-side-1 { grid-column: 2; grid-row: 2; }
.ins-layout-four-row-text .ins-slot-slot-2 { grid-column: 1; grid-row: 3; }
.ins-layout-four-row-text .ins-block-block-side-2 { grid-column: 2; grid-row: 3; }
.ins-layout-four-row-text .ins-slot-slot-3 { grid-column: 1; grid-row: 4; }
.ins-layout-four-row-text .ins-block-block-side-3 { grid-column: 2; grid-row: 4; }
.ins-layout-four-row-text .ins-slot-slot-4 { grid-column: 1; grid-row: 5; }
.ins-layout-four-row-text .ins-block-block-side-4 { grid-column: 2; grid-row: 5; }

/* text only — header, then a single full-page text column */
.ins-layout-text { grid-template-rows: auto 1fr; }
.ins-layout-text .ins-block-block-body { grid-row: 2; }
`.trim();

// ============================================================================
// Strict document schema — the base structural schema plus a refinement that
// rejects any slot/block key not defined by the page's layout. Use this at the
// API boundary; the base inspectionReportDocumentSchema stays purely structural.
// ============================================================================

export const inspectionReportDocumentStrictSchema = inspectionReportDocumentSchema.superRefine(
  (doc, ctx) => {
    doc.pages.forEach((page, pageIndex) => {
      const allowedSlots = new Set(layoutSlotKeys(page.layoutType));
      const allowedBlocks = new Set(layoutBlockKeys(page.layoutType));

      for (const key of Object.keys(page.slots)) {
        if (!allowedSlots.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pages', pageIndex, 'slots', key],
            message: `Slot "${key}" is not defined by layout "${page.layoutType}"`,
          });
        }
      }
      for (const key of Object.keys(page.blocks)) {
        if (!allowedBlocks.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pages', pageIndex, 'blocks', key],
            message: `Block "${key}" is not defined by layout "${page.layoutType}"`,
          });
        }
      }
    });
  },
);

// Autosave payload: the whole document (strictly validated) plus the title and
// status columns. No partial-document patching — the client sends the full doc.
export const updateInspectionReportInputSchema = z.object({
  document: inspectionReportDocumentStrictSchema.optional(),
  title: z.string().optional(),
  status: inspectionReportStatusSchema.optional(),
});
export type UpdateInspectionReportInput = z.infer<typeof updateInspectionReportInputSchema>;
